import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react"

import { useLocalStorage } from "~/src/hooks"
import { isFn } from "~/src/lib/any"

function isValidFormValue<T>(value: unknown): value is T {
  // Since we can't know the exact shape of T at runtime, we just check that it exists
  // The user is responsible for ensuring the type they pass matches what's stored
  return value !== undefined && value !== null
}

type FormStateContextType = {
  state: Record<string, unknown>
  set: (s: Record<string, unknown>) => void
  clearCache: () => void
  resetFormState: () => void
  resetKey: number
}

const FormStateContext = createContext<FormStateContextType | null>(null)

export function FormState({ stateKey, children }: { stateKey: string; children: ReactNode }) {
  const [state, setState, { clearCache }] = useLocalStorage<Record<string, unknown>>(stateKey, {})
  const [resetKey, setResetKey] = useState(0)

  const set = useCallback((s: Record<string, unknown>) => {
    setState((prev) => ({ ...prev, ...s }))
  }, [])

  const resetFormState = useCallback(() => {
    clearCache()
    setState({})
    setResetKey((prev) => prev + 1)
  }, [clearCache, setState])

  return <FormStateContext value={{ state, set, clearCache, resetFormState, resetKey }}>{children}</FormStateContext>
}

export function useClearCache() {
  const context = useContext(FormStateContext)

  if (!context) {
    throw new Error("useFormState must be used within the FormState provider!")
  }

  const { clearCache } = context

  return clearCache
}

export function useResetFormState() {
  const context = useContext(FormStateContext)

  if (!context) {
    throw new Error("useResetFormState must be used within the FormState provider!")
  }

  const { resetFormState } = context

  return resetFormState
}

export function useHasUnsavedChanges() {
  const context = useContext(FormStateContext)

  if (!context) {
    throw new Error("useHasUnsavedChanges must be used within the FormState provider!")
  }

  return context.state.__unsavedChanges__ || false
}

export function useFormState<S>(key: string, initialValue: S | (() => S)) {
  const context = useContext(FormStateContext)
  const initialValueRef = useRef<S | null>(null)
  const lastResetKeyRef = useRef<number>(0)

  if (!context) {
    throw new Error("useFormState must be used within the FormState provider!")
  }

  const { state, set, resetKey } = context

  const rawValue = state[key]
  const value = isValidFormValue<S>(rawValue) ? rawValue : undefined

  useEffect(() => {
    const shouldReset = resetKey > lastResetKeyRef.current
    lastResetKeyRef.current = resetKey

    if (!value || shouldReset) {
      const computedInitialValue = isFn(initialValue) ? initialValue() : initialValue
      initialValueRef.current = computedInitialValue
      set({ [key]: computedInitialValue })
    } else if (initialValueRef.current === null) {
      initialValueRef.current = value
    }
  }, [resetKey])

  const setState = useCallback((s: S) => {
    if (initialValueRef.current !== null && JSON.stringify(s) !== JSON.stringify(initialValueRef.current)) {
      set({ [key]: s, __unsavedChanges__: true })
    } else {
      set({ [key]: s })
    }
  }, [])

  // Since we always provide an initialValue and set it in useEffect,
  // the value should never actually be undefined after the first render
  // We use a non-null assertion here because the type system can't track this
  return [value as S, setState] as [S, (s: S) => void]
}
